/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import {loadGeojson, FEATURED_WORKFLOW_BLOCK_PREFIX} from '@/blocks/featured_workflows/utils';

let featuredWorkflows = []

// DEFORESTATION IN THE AMAZON
const DEFORESTATION_IN_THE_AMAZON_WORKFLOW_ID = "fwb-deforestation-amazon";
const DEFORESTATION_IN_THE_AMAZON_WORKFLOW_XML = `
<xml xmlns="https://developers.google.com/blockly/xml">
    <block type="input_hansen_forest_change" id="fwb-deforestation-amazon" x="20" y="0">
        <field name="param">hansen</field>
        <field name="area">Area 1</field>
        <field name="fd_yearly">2001</field>
        <field name="td_yearly">2020</field>
        <statement name="do">
            <block type="output_other_vis" id="/,qUN93~^,yZhO7^rmHE">
                <field name="layerName">default</field>
            </block>
        </statement>
    </block>
    <block type="input_optical_data" id="fwb-deforestation-amazon-optical" x="20" y="280">
        <field name="dropdown">l8</field>
        <field name="area">Area 1</field>
        <field name="data">_SR</field>
        <field name="fd">2021-01-01</field>
        <field name="td">2021-10-15</field>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <next>
                    <block type="output_optical_vis">
                    </block>
                </next>
            </block>
        </statement>
    </block>

    <block type="input_optical_data" id="fwb-deforestation-amazon-classification" x="20" y="680">
        <field name="dropdown">l8</field>
        <field name="area">Area 1</field>
        <field name="data">_SR</field>
        <field name="fd">2021-01-01</field>
        <field name="td">2021-10-15</field>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <next>
                    <block type="analyse_supervised_classification" id="Q$oB!7L#2Y:.=Zr,mQ">
                        <field name="algorithm">svm</field>
                        <field name="stats_bool">TRUE</field>
                        <field name="stats_params">area_km</field>
                        <field name="kernel">RBF</field>
                        <field name="cost">1</field>
                        <field name="gamma">0.5</field>
                        <statement name="do">
                            <block type="analyse_supervised_class_selector" id="Qi77ZYB{hT;[#!d;-dRt">
                                <field name="area">Area 2</field>
                                <field name="classname">forest</field>
                                <next>
                                    <block type="analyse_supervised_class_selector" id="!_ST[;MVy[{-MNcBhY">
                                        <field name="area">Area 3</field>
                                        <field name="classname">non forest</field>
                                    </block>
                                </next>
                            </block>
                        </statement>
                        <next>
                            <block type="output_other_vis" id="oL-nT_EFNjPZy@L9a3Q~">
                                <field name="layerName">default</field>
                                <next>
                                    <block type="output_table" id="WK-1J^Ed*!H@:E#{~5z:"></block>
                                </next>
                            </block>
                        </next>
                    </block>
                </next>
            </block>
        </statement>
    </block>
</xml>`;

const DEFORESTATION_IN_THE_AMAZON_WORKFLOW_TITLE = 'Deforestation in the Amazon';
const DEFORESTATION_IN_THE_AMAZON_WORKFLOW_DESC =  `This workflow looks at deforestation in the Amazon, both historical activity as a baseline, but also looking at current activity and ongoing deforestation. Earth Blox allows you to use a range of different datasets to achieve this, using the Hansen Global Forest loss data to see the historical deforestation. We are then performing a supervised classification to see more recent activity. The classification provides a simple interface to machine learning algorithms which classify the land cover type of pixels in optical imagery. Supervised classification uses training data defined by labeling user selected areas, and a variety of classification algorithms are available. The classified image is viewed using an Other Data Visualisation block and statistics can be output to the dashboard using a Table block. You can clearly see the deforestation in the underlying imagery, and how this relates to the historical deforestation and the more recent activity.
`;
const DEFORESTATION_IN_THE_AMAZON_WORKFLOW_IMG = 'amazon_deforestation.png';

import DEFORESTATION_IN_THE_AMAZON_AREA from './featured_workflows/areas/DEFORESTATION_IN_THE_AMAZON_AREA'
let DEFORESTATION_IN_THE_AMAZON_GEOJSON = loadGeojson(DEFORESTATION_IN_THE_AMAZON_AREA)

if(DEFORESTATION_IN_THE_AMAZON_GEOJSON !== false){
    const DEFORESTATION_IN_THE_AMAZON_WORKFLOW = {
        id: DEFORESTATION_IN_THE_AMAZON_WORKFLOW_ID,
        xml: DEFORESTATION_IN_THE_AMAZON_WORKFLOW_XML,
        title: DEFORESTATION_IN_THE_AMAZON_WORKFLOW_TITLE,
        desc: DEFORESTATION_IN_THE_AMAZON_WORKFLOW_DESC,
        img: DEFORESTATION_IN_THE_AMAZON_WORKFLOW_IMG,
        area: DEFORESTATION_IN_THE_AMAZON_GEOJSON
    }
    featuredWorkflows.push(DEFORESTATION_IN_THE_AMAZON_WORKFLOW);
}
// EO --- DEFORESTATION IN THE AMAZON

// SIMPLE OPTICAL WORKFLOW
const SIMPLE_OPTICAL_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}simple-optical`;
const SIMPLE_OPTICAL_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
<block type="input_optical_data"  id="${SIMPLE_OPTICAL_WORKFLOW_ID}" x="0" y="0">
    <field name="dropdown">l8</field>
    <field name="area">UK</field>
    <field name="data">_SR</field>
    <field name="fd">2020-01-31</field>
    <field name="td">2021-02-01</field>
    <comment pinned="true" h="120" w="350">You can select from a list of satellites. Landsat is the longest running series of Earth observation satellites designed for looking at the land surface. Sentinel 2 is more recent with higher resolution data.</comment>
    <statement name="do">
        <block type="analyse_cloud_filter" >
            <field name="filterType">L8_SR_Cloud_Mask</field>
            <field name="L8_SR_Cloud_Mask_cloud_cover">20</field>
            <next>
                <block type="output_optical_vis"  id="HCH~bhdzk@=}k1.G]@TR">
                    <field name="bandR">SR_B4</field>
                    <field name="bandG">SR_B3</field>
                    <field name="bandB">SR_B2</field>
                    <field name="layerName">default</field>
                    <comment pinned="false" h="100" w="250">You can use the drop down menu to choose other detectors, including ones in the infrared part of the spectrum.  </comment>
                </block>
            </next>
        </block>
    </statement>
</block>
</xml>`;

const SIMPLE_OPTICAL_WORKFLOW_TITLE = 'Landsat 8 true colour image';
const SIMPLE_OPTICAL_WORKFLOW_DESC = `Landsat is the longest running series of Earth observation satellites designed for looking at the land surface.  This workflow displays 3 of the colour measurements made by the most recent Landsat, the 8th in the series, and replicates what a normal photograph would look like.  For Landsat 8, these are the data from the red, green and blue detectors. You can use the drop down menu to choose other detectors, including ones in the infrared part of the spectrum. Surface Reflectance data options means the image is corrected to remove the effects of the atmosphere, and represents the true colour of the surface of the Earth. This example looks at the UK, but you can choose other countries from the drop down menu. `;
const SIMPLE_OPTICAL_WORKFLOW_IMG = 'Simple-Optical.png';


const SIMPLE_OPTICAL_WORKFLOW = {
    id: SIMPLE_OPTICAL_WORKFLOW_ID,
    xml: SIMPLE_OPTICAL_WORKFLOW_XML,
    title: SIMPLE_OPTICAL_WORKFLOW_TITLE,
    desc: SIMPLE_OPTICAL_WORKFLOW_DESC,
    img: SIMPLE_OPTICAL_WORKFLOW_IMG,
}

featuredWorkflows.push(SIMPLE_OPTICAL_WORKFLOW);
// EO --- SIMPLE OPTICAL WORKFLOW

// FALSE COLOUR OPTICAL WORKFLOW
const FALSE_COLOUR_OPTICAL_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}false-optical`;
const FALSE_COLOUR_OPTICAL_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
<block type="input_optical_data"  id="${FALSE_COLOUR_OPTICAL_WORKFLOW_ID}" x="0" y="0">
    <field name="dropdown">l8</field>
    <field name="area">UK</field>
    <field name="data">_SR</field>
    <field name="fd">2020-01-31</field>
    <field name="td">2021-02-01</field>
    <comment pinned="false" h="120" w="350">You can select from a list of satellites. Landsat is the longest running series of Earth observation satellites designed for looking at the land surface. Sentinel 2 is more recent with higher resolution data.</comment>
    <statement name="do">
        <block type="analyse_cloud_filter" >
            <field name="filterType">L8_SR_Cloud_Mask</field>
            <field name="L8_SR_Cloud_Mask_cloud_cover">20</field>
            <next>
                <block type="output_optical_vis"  id="HCH~bhdzk@=}k1.G]@TR">
                    <field name="bandR">SR_B5</field>
                    <field name="bandG">SR_B4</field>
                    <field name="bandB">SR_B3</field>
                    <field name="layerName">default</field>
                    <comment pinned="false" h="100" w="250">You can use the drop down menu to choose other detectors, including ones in the infrared part of the spectrum.  </comment>
                </block>
            </next>
        </block>
    </statement>
</block>
</xml>`;
const FALSE_COLOUR_OPTICAL_WORKFLOW_TITLE = 'False Colour Imagery';
const FALSE_COLOUR_OPTICAL_WORKFLOW_DESC = 'The Optical Visualisation block allows you to view more than just the Red, Green, and Blue bands of an image. By using different combinations of bands and colour channels, we can observe different image properties. This workflow will clearly highlight vegetation as red, for example.';
const FALSE_COLOUR_OPTICAL_WORKFLOW_IMG = 'False-Colour-Optical.png';


const FALSE_COLOUR_OPTICAL_WORKFLOW = {
    id: FALSE_COLOUR_OPTICAL_WORKFLOW_ID,
    xml: FALSE_COLOUR_OPTICAL_WORKFLOW_XML,
    title: FALSE_COLOUR_OPTICAL_WORKFLOW_TITLE,
    desc: FALSE_COLOUR_OPTICAL_WORKFLOW_DESC,
    img: FALSE_COLOUR_OPTICAL_WORKFLOW_IMG,
}
featuredWorkflows.push(FALSE_COLOUR_OPTICAL_WORKFLOW);

// EO --- FALSE COLOUR OPTICAL WORKFLOW

// SIMPLE RADAR WORKFLOW
const SIMPLE_RADAR_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}simple-radar`;
const SIMPLE_RADAR_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
<block type="input_radar_data"  id="${SIMPLE_RADAR_WORKFLOW_ID}" x="16" y="146">
    <field name="dropdown">SAR</field>
    <field name="param">dB</field>
    <field name="area">UK</field>
    <field name="fd">2021-02-16</field>
    <field name="td">2021-03-01</field>
    <field name="orbit_dropdown">BOTH</field>
    <comment pinned="false" h="120" w="260">Sentinel-1 is the most recent radar imaging satellite from the European Space Agency. By using microwaves, the radar system can see through clouds and at night.</comment>
    <statement name="do">
        <block type="output_radar_vis"  id="?8G?h_3Hr9?~YIlNWQ%#">
            <field name="r">VV</field><field name="g">VV</field>
            <field name="b">VV</field><field name="param">.mean()</field>
            <field name="layerName">default</field>
            <comment pinned="true" h="120" w="260">Radar data visualisation. You can assign different sensors to the RGB channel in the output. SAR HH and HV data is only available in the polar regions.</comment>
            </block>
    </statement>
</block>
</xml>`;
const SIMPLE_RADAR_WORKFLOW_TITLE = 'Radar Imagery';
const SIMPLE_RADAR_WORKFLOW_DESC = 'This workflow shows an average image across the entire UK.  You can use the drop down menu to choose another country or select “ADD AREA” to create your own customised area.';
const SIMPLE_RADAR_WORKFLOW_IMG = 'Simple-Radar.png';

const SIMPLE_RADAR_WORKFLOW = {
    id: SIMPLE_RADAR_WORKFLOW_ID,
    xml: SIMPLE_RADAR_WORKFLOW_XML,
    title: SIMPLE_RADAR_WORKFLOW_TITLE,
    desc: SIMPLE_RADAR_WORKFLOW_DESC,
    img: SIMPLE_RADAR_WORKFLOW_IMG,
}
featuredWorkflows.push(SIMPLE_RADAR_WORKFLOW);
// EO --- SIMPLE RADAR WORKFLOW

// INDICES WORKFLOW
const INDICES_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}indices`;
const INDICES_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
    <block type="input_optical_data" id="${FEATURED_WORKFLOW_BLOCK_PREFIX}ndsi" movable="true" x="51" y="10">
        <field name="dropdown">l8</field>
        <field name="area">UK</field>
        <field name="data">_SR</field>
        <field name="fd">2020-01-29</field>
        <field name="td">2021-01-30</field>
        <comment pinned="false" h="120" w="320">You can select from a list of satellites. Landsat is the longest running series of Earth observation satellites designed for looking at the land surface. Sentinel 2 is more recent with higher resolution data.</comment>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <field name="filterType">L8_SR_Cloud_Mask</field>
                <field name="L8_SR_Cloud_Mask_cloud_cover">20</field>
                <next>
                    <block type="analyse_ndsi"  id="Z@]yhcT_v(ZhWTUMt)+r">
                        <field name="type">mean</field>
                        <comment pinned="true" h="85" w="165">You can choose a different statistical method.</comment>
                        <next>
                            <block type="output_other_vis"  id="9CV06%T61-:W:TQ(ir/c">
                                <field name="layerName">NDSI</field>
                            </block>
                        </next>
                    </block>
                </next>
            </block>
        </statement>
    </block>

    <block type="input_optical_data" id="${FEATURED_WORKFLOW_BLOCK_PREFIX}ndwi" movable="true" x="51" y="400">
        <field name="dropdown">s2</field>
        <field name="area">UK</field>
        <field name="data">_SR</field>
        <field name="fd">2020-01-29</field>
        <field name="td">2021-01-30</field>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <field name="filterType">qa_s2_SR</field>
                <field name="qa_s2_SR_cloud_cover">20</field>
                <next>
                    <block type="analyse_ndwi"  id="8t@=qxThaeaut6xqL[C">
                        <field name="type">mean</field>
                        <next>
                            <block type="output_other_vis"  id="50#bOQXb]OFK9nMfy5%x">
                                <field name="layerName">NDWI</field>
                                <comment pinned="true" h="80" w="230">Open the layers in the Map interface by pressing the layer icon.</comment>
                            </block>
                        </next>
                    </block>
                </next>
            </block>
        </statement>
    </block>

    <block type="input_optical_data" id="${FEATURED_WORKFLOW_BLOCK_PREFIX}nbi" movable="true" x="50" y="790">
        <field name="dropdown">l8</field>
        <field name="area">UK</field>
        <field name="data">_SR</field>
        <field name="fd">2020-01-29</field>
        <field name="td">2021-01-30</field>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <field name="filterType">L8_SR_Cloud_Mask</field>
                <field name="L8_SR_Cloud_Mask_cloud_cover">20</field>
                <next>
                    <block type="analyse_nbi"  id="Qpc!DlJCv$MzM(AN-v">
                        <field name="type">mean</field>
                        <comment pinned="true" h="85" w="235">Open the legend icon on the Map interface to see the map legend for each index.</comment>
                        <next>
                            <block type="output_other_vis"  id="N!I/MSFSjpw,I0Nioq+y">
                                <field name="layerName">NBI</field>
                            </block>
                        </next>
                    </block>
                </next>
            </block>
        </statement>
    </block>

    <block type="input_optical_data" id="${FEATURED_WORKFLOW_BLOCK_PREFIX}ndvi" movable="true" x="50" y="1180">
        <field name="dropdown">l8</field>
        <field name="area">UK</field>
        <field name="data">_SR</field>
        <field name="fd">2020-01-29</field>
        <field name="td">2021-01-30</field>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <field name="filterType">L8_SR_Cloud_Mask</field>
                <field name="L8_SR_Cloud_Mask_cloud_cover">20</field>
                <next>
                    <block type="analyse_ndvi"  id="Q._u^vkLuU-/dtd5w7@t">
                        <field name="type">mean</field>
                        <next>
                            <block type="output_other_vis"  id=",{/)+{%]mn_-nV/CWoW">
                                <field name="layerName">NDVI</field>
                            </block>
                        </next>
                    </block>
                </next>
            </block>
        </statement>
    </block>
</xml>`;

const INDICES_WORKFLOW_TITLE = 'Optical Indices';
const INDICES_WORKFLOW_DESC = 'Indices are simple combinations of measurement bands used to indicate various properties of the Earth’s surface. The available blocks have indices designed to determine the proportion of vegetation, water, snow, and burn marks from fires. The Indices blocks can be used with any available satellite in the optical data block. Like all the analysis blocks, the indices blocks connect between Input and Output blocks such as optical data and other visualisation.';
const INDICES_WORKFLOW_IMG = 'Indices-four.png';

const INDICES_WORKFLOW = {
    id: INDICES_WORKFLOW_ID,
    xml: INDICES_WORKFLOW_XML,
    title: INDICES_WORKFLOW_TITLE,
    desc: INDICES_WORKFLOW_DESC,
    img: INDICES_WORKFLOW_IMG
}

featuredWorkflows.push(INDICES_WORKFLOW);
// EO --- INDICES WORKFLOW

// SUPERVISED CLASSIFICATION WORKFLOW
const SUPERVISED_CLASSIFICATION_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}supervised-classification`;
const SUPERVISED_CLASSIFICATION_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
    <block type="input_optical_data" id="${SUPERVISED_CLASSIFICATION_WORKFLOW_ID}"  x="43" y="53">
        <field name="dropdown">s2</field>
        <field name="area">Area 1</field>
        <field name="data">_SR</field>
        <field name="fd">2020-02-20</field>
        <field name="td">2021-02-21</field>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <field name="filterType">qa_s2_SR</field>
                <field name="qa_s2_SR_cloud_cover">20</field>
                <next>
                    <block type="analyse_supervised_classification" id="ls8Rm3?|TS;M#4laq~4/" >
                        <field name="algorithm">svm</field>
                        <field name="stats_bool">TRUE</field>
                        <field name="stats_params">area_m</field>
                        <statement name="do">
                            <block type="analyse_supervised_class_selector" id="pJ*#uT{nEGh2uUCGr,P" >
                                <field name="area">Area 2</field>
                                <field name="classname">Urban</field>
                                <next>
                                    <block type="analyse_supervised_class_selector" id="kt%,ZG+=!ytW96$+iRu3" >
                                        <field name="area">Area 3</field>
                                        <field name="classname">Vegetation</field>
                                        <next>
                                            <block type="analyse_supervised_class_selector" id="dl@;*u38MX}z8^$1,=a{" >
                                                <field name="area">Area 4</field>
                                                <field name="classname">Water</field>
                                            </block>
                                        </next>
                                    </block>
                                </next>
                            </block>
                        </statement>
                        <next>
                            <block type="output_other_vis" id="U(q5%H.t7*YzwBC4ahiR" >
                                <field name="layerName">default</field>
                                <next>
                                    <block type="output_table" id="}{+?)x57/UhY/H$5scG@" >
                                    </block>
                                </next>
                            </block>
                        </next>
                    </block>
                </next>
            </block>
        </statement>
    </block>
</xml>`;



const SUPERVISED_CLASSIFICATION_WORKFLOW_TITLE = 'Supervised Classification';
const SUPERVISED_CLASSIFICATION_WORKFLOW_DESC = 'The classification block provides a simple interface to machine learning algorithms which classify the land cover type of pixels in optical imagery. Supervised classification uses training data defined by labeling user selected areas, and a variety of classification algorithms are available. The classified image is viewed using an Other Data Visualisation block and statistics can be output to the dashboard using a Table block.';
const SUPERVISED_CLASSIFICATION_WORKFLOW_IMG = 'supervised.png';
import SUPERVISED_CLASSIFICATION_WORKFLOW_AREA from './featured_workflows/areas/SUPERVISED_CLASSIFICATION_WORKFLOW_AREA.js'
let SUPERVISED_CLASSIFICATION_WORKFLOW_GEOJSON = loadGeojson(SUPERVISED_CLASSIFICATION_WORKFLOW_AREA)

if(SUPERVISED_CLASSIFICATION_WORKFLOW_GEOJSON !== false){
    const SUPERVISED_CLASSIFICATION_WORKFLOW = {
        id: SUPERVISED_CLASSIFICATION_WORKFLOW_ID,
        xml: SUPERVISED_CLASSIFICATION_WORKFLOW_XML,
        title: SUPERVISED_CLASSIFICATION_WORKFLOW_TITLE,
        desc: SUPERVISED_CLASSIFICATION_WORKFLOW_DESC,
        img: SUPERVISED_CLASSIFICATION_WORKFLOW_IMG,
        area: SUPERVISED_CLASSIFICATION_WORKFLOW_GEOJSON
    }
    featuredWorkflows.push(SUPERVISED_CLASSIFICATION_WORKFLOW);
}

// // EO --- SUPERVISED CLASSIFICATION WORKFLOW

// FIRES LANDCOVER DASHBOARD FEATURED WORKFLOW
const FIRES_LANDCOVER_DASHBOARD_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}fires-landcover-dashboard`;
const FIRES_LANDCOVER_DASHBOARD_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
<block type="input_fire_data" id="${FIRES_LANDCOVER_DASHBOARD_ID}"  x="45" y="31">
    <field name="dropdown">firms</field>
    <field name="area">Area 1</field>
    <field name="fd">2020-05-17</field>
    <field name="td">2020-10-18</field>
    <comment pinned="false" h="62" w="390">The fires datasets show regions which have been burning, up to near real-time.</comment>
    <statement name="do">
        <block type="analyse_fires_to_land_comparison" id="V^F3:ZuKxzN{OR_a/r7y" >
        <field name="dropdownLand">copernicus_cover_2019</field>
        <field name="check_40">TRUE</field>
        <field name="check_60">TRUE</field>
        <field name="check_111">TRUE</field>
        <field name="check_112">TRUE</field>
        <field name="check_113">TRUE</field>
        <field name="check_114">TRUE</field>
        <field name="check_115">TRUE</field>
        <field name="check_116">TRUE</field>
        <field name="check_121">TRUE</field>
        <field name="check_122">TRUE</field>
        <field name="check_123">TRUE</field>
        <field name="check_124">TRUE</field>
        <field name="check_125">TRUE</field>
        <field name="check_126">TRUE</field>
        <field name="check_20">TRUE</field>
        <field name="check_30">TRUE</field>
        <field name="check_50">TRUE</field>
        <field name="check_70">TRUE</field>
        <field name="check_80">TRUE</field>
        <field name="check_200">TRUE</field>
        <field name="check_90">TRUE</field>
        <field name="check_100">TRUE</field>
        <field name="check_0">TRUE</field>
        <comment pinned="false" h="115" w="436">The Comparison block allows you to choose landcover typed from the Copernicus Global Land Cover Map, which has 100m resolution. Each pixel has a value which corresponds to a different landcover type. The block compares where fires occur on selected classes. </comment>
        <statement name="do">
            <block type="output_other_vis" id="qCIu!bT}gzh.PW96^=(E" >
                <field name="layerName">default</field>
                <comment pinned="false" h="74" w="429">The Other Data Visualisation block produces a map layer showing the areas in which fires have occurred over the selected landcover types.</comment>
                <next>
                    <block type="output_table" id="EZvWY03c(lq#r*/4sNC" >
                    <comment pinned="false" h="93" w="498">The Table block generates tables in the Dashboard tab which summarise the statistics calculate in the Comparison block. Other dashboard functionalities such as charts are available in the full version of Earth Blox!</comment>
                    </block>
                </next>
            </block>
        </statement>
        </block>
    </statement>
</block>
</xml>`;
const FIRES_LANDCOVER_DASHBOARD_TITLE = 'Fires Land Cover Dashboard';
const FIRES_LANDCOVER_DASHBOARD_DESC = 'The comparison block allows us to determine the type of areas burning in fires. The land cover data set classifies each pixel into one of several categories of land use/vegetation cover, and by comparing this to datasets of fires and burnt areas, the block produces a breakdown of fires by land cover type.';
const FIRES_LANDCOVER_DASHBOARD_IMG = 'Fires-Land-Cover-Dashboard.png';
import FIRES_LANDCOVER_DASHBOARD_AREA from './featured_workflows/areas/FIRES_LANDCOVER_DASBOARD_AREA.js'
let FIRES_LANDCOVER_DASHBOARD_GEOJSON = loadGeojson(FIRES_LANDCOVER_DASHBOARD_AREA)

if(FIRES_LANDCOVER_DASHBOARD_GEOJSON !== false){
    const FIRES_LANDCOVER_DASHBOARD = {
        id: FIRES_LANDCOVER_DASHBOARD_ID,
        xml: FIRES_LANDCOVER_DASHBOARD_XML,
        title: FIRES_LANDCOVER_DASHBOARD_TITLE,
        desc: FIRES_LANDCOVER_DASHBOARD_DESC,
        img: FIRES_LANDCOVER_DASHBOARD_IMG,
        area: FIRES_LANDCOVER_DASHBOARD_GEOJSON
    }

    featuredWorkflows.push(FIRES_LANDCOVER_DASHBOARD);
}
// EO --- FIRES LANDCOVER DASHBOARD WORKFLOW

// OPTICAL TIMESERIES WORKFLOW
const OPTICAL_TIMESERIES_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}optical-timeseries`;
const OPTICAL_TIMESERIES_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
    <block type="input_optical_data" id="${OPTICAL_TIMESERIES_WORKFLOW_ID}"  x="0" y="0">
        <field name="dropdown">s2</field>
        <field name="area">NA</field>
        <field name="data">_SR</field>
        <field name="fd">2019-06-01</field>
        <field name="td">2020-06-01</field>
        <comment pinned="true" h="90" w="370">You can add custom areas on the map using the "Add area" button. It is possible to then select the area you created from the "Area" dropdown in this block.</comment>
        <statement name="do">
            <block type="analyse_cloud_filter" >
                <field name="filterType">qa_s2_SR</field>
                <field name="qa_s2_SR_cloud_cover">20</field>
                <next>
                    <block type="analyse_timeseries" id="?46w~t*!LVmJi@IUrk;U" >
                        <field name="interval">monthly</field>
                        <comment pinned="false" h="100" w="350">You can choose a time series interval. Each time interval will produce one image in the time series. The "Yearly" option will use data for the entire calendar year.</comment>
                        <statement name="statements">
                            <block type="output_optical_vis" id=".Uymk+u^6DCeo-6M7+Ym" >
                                <field name="bandR">B4</field>
                                <field name="bandG">B3</field>
                                <field name="bandB">B2</field>
                                <field name="layerName">default</field>
                                <comment pinned="false" h="90" w="350">You can specify a name for the map layer. If you leave this as default, Earth Blox generates a layer name for you based on the band layers.</comment>
                            </block>
                        </statement>
                    </block>
                </next>
            </block>
        </statement>
    </block>
</xml>`;
const OPTICAL_TIMESERIES_WORKFLOW_TITLE = 'Optical Time Series';
const OPTICAL_TIMESERIES_WORKFLOW_DESC = 'Using a time series block we can look at the change over a selected time period. This workflow shows a composite image for each month over a year, using the average pixel value in the month to generate the image. The slider at the bottom of the map window allows you to select individual images. You can view the series of images using the play button, and pause at any point.';
const OPTICAL_TIMESERIES_WORKFLOW_IMG = 'Optical-Timeseries.png';
import OPTICAL_TIMESERIES_WORKFLOW_AREA from './featured_workflows/areas/OPTICAL_TIMESERIES_WORKFLOW_AREA.js';

let OPTICAL_TIMESERIES_WORKFLOW_GEOJSON = loadGeojson(OPTICAL_TIMESERIES_WORKFLOW_AREA)

if(OPTICAL_TIMESERIES_WORKFLOW_GEOJSON !== false) {
    const OPTICAL_TIMESERIES_WORKFLOW = {
        id: OPTICAL_TIMESERIES_WORKFLOW_ID,
        xml: OPTICAL_TIMESERIES_WORKFLOW_XML,
        title: OPTICAL_TIMESERIES_WORKFLOW_TITLE,
        desc: OPTICAL_TIMESERIES_WORKFLOW_DESC,
        img: OPTICAL_TIMESERIES_WORKFLOW_IMG,
        area: OPTICAL_TIMESERIES_WORKFLOW_GEOJSON
    }
    featuredWorkflows.push(OPTICAL_TIMESERIES_WORKFLOW);
}


// EO --- OPTICAL TIMESERIES WORKFLOW

// RADAR TIMESERIES WORKFLOW
const RADAR_TIMESERIES_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}radar-timeseries`;
const RADAR_TIMESERIES_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
    <block type="input_radar_data"  id="${RADAR_TIMESERIES_WORKFLOW_ID}" x="124" y="85">
        <field name="dropdown">SAR</field>
        <field name="param">dB</field>
        <field name="area">Area 1</field>
        <field name="fd">2020-01-01</field>
        <field name="td">2021-01-01</field>
        <field name="orbit_dropdown">BOTH</field>
        <comment pinned="false" h="120" w="260">Sentinel-1 is the most recent radar imaging satellite from the European Space Agency. By using microwaves, the radar system can see through clouds and at night.</comment>
        <statement name="do">
            <block type="analyse_timeseries"  id="7m(l,ViIOz}fS1o--h2!">
                <field name="interval">monthly</field>
                <comment pinned="false" h="100" w="350">You can choose a time series interval. Each time interval will produce one image in the time series. The "Yearly" option will use data for the entire calendar year.</comment>
                <statement name="statements">
                    <block type="output_radar_vis"  id="SkXpKT9d5_WFyxfWq_bJ">
                        <field name="r">VV</field>
                        <field name="g">VV</field>
                        <field name="b">VV</field>
                        <field name="param">.mean()</field>
                        <field name="layerName">default</field>
                        <comment pinned="false" h="120" w="260">Radar data visualisation. You can assign different sensors to the RGB channel in the output. SAR HH and HV data only available only in the polar regions.</comment>
                    </block>
                </statement>
            </block>
        </statement>
    </block>
</xml>`;
const RADAR_TIMESERIES_WORKFLOW_TITLE = 'Radar Time Series';
const RADAR_TIMESERIES_WORKFLOW_DESC = 'Using the time series block we can observe the changes over the Okavango Delta observed by Sentinel 1.';
const RADAR_TIMESERIES_WORKFLOW_IMG = 'Radar-Timeseries.png';
import RADAR_TIMESERIES_WORKFLOW_AREA from './featured_workflows/areas/RADAR_TIMESERIES_WORKFLOW_AREA.js'

let RADAR_TIMESERIES_WORKFLOW_GEOJSON = loadGeojson(RADAR_TIMESERIES_WORKFLOW_AREA)

if(RADAR_TIMESERIES_WORKFLOW_GEOJSON !== false) {
    const RADAR_TIMESERIES_WORKFLOW = {
        id: RADAR_TIMESERIES_WORKFLOW_ID,
        xml: RADAR_TIMESERIES_WORKFLOW_XML,
        title: RADAR_TIMESERIES_WORKFLOW_TITLE,
        desc: RADAR_TIMESERIES_WORKFLOW_DESC,
        img: RADAR_TIMESERIES_WORKFLOW_IMG,
        area: RADAR_TIMESERIES_WORKFLOW_GEOJSON
    }
    featuredWorkflows.push(RADAR_TIMESERIES_WORKFLOW);
}

// EO --- RADAR TIMESERIES WORKFLOW

// S1 TYPHOON IDAI WORKFLOW
const S1_TYPHOON_IDAI_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}s1-typhoon-idai`;
const S1_TYPHOON_IDAI_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
<block type="input_radar_data" id="${S1_TYPHOON_IDAI_WORKFLOW_ID}" x="35" y="175" >
    <field name="dropdown">SAR</field>
    <field name="param">dB</field>
    <field name="area">Area 1</field>
    <field name="fd">2019-01-01</field>
    <field name="td">2019-06-30</field>
    <field name="orbit_dropdown">BOTH</field>
    <comment pinned="true" h="105.60769653320312" w="442.4739685058594">Use this block to see the area of flooding after typhoon Idai in 2019 that killed 215 people and affected 1.5 million. The very dark area inland of the river mouth turns very black in March 2019. The black area is the extent of the flooding.</comment>
    <statement name="do">
        <block type="analyse_timeseries" id="R)zZxjgG6}S^D7.axaR" >
        <field name="interval">monthly</field>
        <statement name="statements">
            <block type="output_radar_vis" id="9dT.WK*k9gY@a#@Ag}}[" >
            <field name="r">VV</field>
            <field name="g">VV</field>
            <field name="b">VV</field>
            <field name="param">.mean()</field>
            <field name="layerName">default</field>
            </block>
        </statement>
        </block>
        </statement>
</block>
</xml>`;
const S1_TYPHOON_IDAI_WORKFLOW_TITLE = 'S1 Typhoon Idai Flooding';
const S1_TYPHOON_IDAI_WORKFLOW_DESC = 'Use this workflow to see the area of flooding after typhoon Idai in 2019 that killed 215 people and affected 1.5 million. The very dark area inland of the river mouth turns very black in March 2019. The black area is the extent of the flooding.';
const S1_TYPHOON_IDAI_WORKFLOW_IMG = 'S1-Typhoon-Idai.png';
import S1_TYPHOON_IDAI_WORKFLOW_AREA from './featured_workflows/areas/S1_TYPHOON_IDAI_WORKFLOW_AREA.js'

let S1_TYPHOON_IDAI_WORKFLOW_GEOJSON = loadGeojson(S1_TYPHOON_IDAI_WORKFLOW_AREA)

if(S1_TYPHOON_IDAI_WORKFLOW_GEOJSON !== false) {
    const S1_TYPHOON_IDAI_WORKFLOW = {
        id: S1_TYPHOON_IDAI_WORKFLOW_ID,
        xml: S1_TYPHOON_IDAI_WORKFLOW_XML,
        title: S1_TYPHOON_IDAI_WORKFLOW_TITLE,
        desc: S1_TYPHOON_IDAI_WORKFLOW_DESC,
        img: S1_TYPHOON_IDAI_WORKFLOW_IMG,
        area: S1_TYPHOON_IDAI_WORKFLOW_GEOJSON
    }
    featuredWorkflows.push(S1_TYPHOON_IDAI_WORKFLOW);
}
// EO --- S1 TYPHOON IDAI WORKFLOW

// S1 SHIPS WORKFLOW
const S1_SHIPS_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}s1-ships`;
const S1_SHIPS_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
    <block type="input_radar_data" id="${S1_SHIPS_WORKFLOW_ID}" x="16" y="149" >
        <field name="dropdown">SAR</field>
        <field name="param">dB</field>
        <field name="area">Area 1</field>
        <field name="fd">2020-01-01</field>
        <field name="td">2021-01-01</field>
        <field name="orbit_dropdown">BOTH</field>
        <comment pinned="true" h="69.4053955078125" w="339.22308349609375">This workflow creates an image of the maximum pixel values over a 12 month period. </comment>
        <statement name="do">
            <block type="output_radar_vis" id="_J5%A3DXVBec[}tQ%+" >
                <field name="r">VV</field>
                <field name="g">VV</field>
                <field name="b">VV</field>
                <field name="param">.max()</field>
                <field name="layerName">default</field>
            </block>
        </statement>
    </block>
</xml>`;
const S1_SHIPS_WORKFLOW_TITLE = 'Shipping lanes through the English Channel';
const S1_SHIPS_WORKFLOW_DESC = 'Sentinel-1 uses a microwave radar system to image the surface.  Large ships create a particularly bright signal and exhibit a strong contrast with the dark signal of the sea.  In this workflow, 12 months of Sentinel 1 radar images over the English Channel are analysed and only the brightest pixel values are shown in the image (since the “max” has been selected).  The result is that all the ships that have been imaged in the 12 month period appear in the image, so that the main shipping routes are clearly visible, as are the Dover-Calais ferry routes.  By choosing “ADD AREA” and selecting the new area from the drop-down menu, you might also like to view the Straights of Gibraltar or the entry points to the Suez and Panama canals.';
const S1_SHIPS_WORKFLOW_IMG = 'S1-Ships.png';
import S1_SHIPS_WORKFLOW_AREA from './featured_workflows/areas/S1_SHIPS_WORKFLOW_AREA.js'

let S1_SHIPS_WORKFLOW_GEOJSON = loadGeojson(S1_SHIPS_WORKFLOW_AREA)

if(S1_SHIPS_WORKFLOW_GEOJSON !== false) {
    const S1_SHIPS_WORKFLOW = {
        id: S1_SHIPS_WORKFLOW_ID,
        xml: S1_SHIPS_WORKFLOW_XML,
        title: S1_SHIPS_WORKFLOW_TITLE,
        desc: S1_SHIPS_WORKFLOW_DESC,
        img: S1_SHIPS_WORKFLOW_IMG,
        area: S1_SHIPS_WORKFLOW_GEOJSON
    }
    featuredWorkflows.push(S1_SHIPS_WORKFLOW);
}

// EO --- S1 SHIPS WORKFLOW

// DEM WORKFLOW
const DEM_WORKFLOW_ID = `${FEATURED_WORKFLOW_BLOCK_PREFIX}dem-dem`;
const DEM_WORKFLOW_XML = `<xml xmlns="https://developers.google.com/blockly/xml">
<block type="input_dem_data" id="${DEM_WORKFLOW_ID}" x="0" y="0" movable="true">
    <field name="dropdown">srtm_dtm</field>
    <field name="area">Area 1</field>
    <statement name="do">
        <block type="output_dem_vis" id="zzFb(S8FGWlI5d]F_*5P" >
        <field name="param">dem</field>
        <field name="layerName">default</field>
        </block>
    </statement>
</block>
<block type="input_dem_data" id="${FEATURED_WORKFLOW_BLOCK_PREFIX}dem-slope" x="0" y="180" movable="true">
    <field name="dropdown">srtm_dtm</field>
    <field name="area">Area 1</field>
    <statement name="do">
        <block type="output_dem_vis" id="WM$WlHv6pC={(]/fb)6@" >
        <field name="param">slope</field>
        <field name="layerName">default</field>
        </block>
    </statement>
</block>
<block type="input_dem_data" id="${FEATURED_WORKFLOW_BLOCK_PREFIX}dem-hillshade" x="0" y="360" movable="true">
    <field name="dropdown">srtm_dtm</field>
    <field name="area">Area 1</field>
    <statement name="do">
        <block type="output_dem_vis" id="?s}ImPlGO.zfN3D-sS*Z" >
        <field name="param">hillshade</field>
        <field name="layerName">default</field>
        </block>
    </statement>
</block>
</xml>`;
const DEM_WORKFLOW_TITLE = 'The Changing Course of the Mississippi River';
const DEM_WORKFLOW_DESC = 'This workflow looks at the impact that the changing course of the Mississippi River has on the terrain. Earth Blox allows you to use Digital Elevation Models to look at the terrain and slope to understand more about this complex and interesting landscape. You can clearly see the current course of the river. However the Digital Elevation Model data reveals previous paths of the river, which has subsequently changed and left behind ox bow lakes and patterns in the landscape.';
const DEM_WORKFLOW_IMG = 'DEM.png';
import DEM_WORKFLOW_AREA from './featured_workflows/areas/DEM_WORKFLOW_AREA.js'

let DEM_WORKFLOW_GEOJSON = loadGeojson(DEM_WORKFLOW_AREA)

if(DEM_WORKFLOW_GEOJSON !== false) {
    const DEM_WORKFLOW = {
        id: DEM_WORKFLOW_ID,
        xml: DEM_WORKFLOW_XML,
        title: DEM_WORKFLOW_TITLE,
        desc: DEM_WORKFLOW_DESC,
        img: DEM_WORKFLOW_IMG,
        area: DEM_WORKFLOW_GEOJSON
    }
    featuredWorkflows.push(DEM_WORKFLOW);
}

// EO --- DEM WORKFLOW

export { featuredWorkflows }
